import { AIBeginnersContent } from './aiBeginnersSeed';

export const AI_MARKETING_SEED: AIBeginnersContent = {
  sessions: [
    {
      number: 1,
      slug: "strategy-foundation",
      title: "Strategy Foundation",
      subtitle: "Leave with a measurable campaign goal, a brand voice card, and a 4-tier KPI model — the foundation every other session builds on.",
      outcome: "Leave with a measurable campaign goal, a brand voice card, and a 4-tier KPI model.",
      exercises: [
        {
          id: "m1e1",
          number: 1,
          title: "Write Your Campaign Goal",
          instructions: `**Goal of this exercise**\nTo build a measurable, 4-part goal for your marketing campaign.\n\n**What you are doing**\nYou will use the formula *Action + Audience + Outcome + Timeframe* to define exactly what your campaign needs to accomplish. You will paste the provided prompt into AI and refine the output to ensure it's specific and realistic.\n\n**Prompt:**\n*"I'm building a marketing campaign for [your business/product/service]. My target audience is [who you serve]. The main thing I want this campaign to accomplish is [leads/sales/awareness]. My timeline is [timeframe] and my budget is [range]. Write me a one-sentence campaign goal using this formula: Action + Audience + Outcome + Timeframe."*`,
          artifact: "Your one-sentence campaign goal.",
          artifactRequired: true,
        },
        {
          id: "m1e2",
          number: 2,
          title: "Build Your Avatar and Brand Voice",
          instructions: `**Goal of this exercise**\nTo define your ideal customer and establish your brand's 4-dimensional voice.\n\n**What you are doing**\nYou will generate an avatar summary and a brand voice card (Tone, Language, Personality, Purpose) that you will use in all future AI prompts.\n\n**Prompt 1 (Avatar):**\n*"I sell [product] to [audience]. Create a one-paragraph avatar summary for my ideal customer. Include: demographics, biggest frustration, their exact wording of the problem, and what would make them say 'finally, someone gets it'."*\n\n**Prompt 2 (Voice):**\n*"My audience is [paste avatar]. Here's how I want to come across: [2-3 sentences]. Create a brand voice card with four dimensions: Tone, Language, Personality, Purpose. Include a one-sentence example for each."*`,
          artifact: "Avatar summary paragraph + brand voice card with all 4 dimensions and examples.",
          artifactRequired: true,
        },
        {
          id: "m1e3",
          number: 3,
          title: "Build Your KPI Model",
          instructions: `**Goal of this exercise**\nTo establish metrics across four tiers so you can accurately track campaign success.\n\n**What you are doing**\nYou will use your Campaign Goal to build a 4-Tier KPI Model (Reach, Engagement, Conversion, Retention) and establish realistic benchmarks for your industry.\n\n**Prompt:**\n*"My campaign goal is: [paste goal]. My business is [industry] and my primary channels are [channels]. Build me a 4-tier KPI model: 2–3 specific metrics per tier, with a realistic benchmark target for a [size] business. Format as a table."*`,
          artifact: "4-tier KPI table with benchmark targets.",
          artifactRequired: true,
        }
      ]
    },
    {
      number: 2,
      slug: "buyer-personas-journeys",
      title: "Buyer Personas & Journeys",
      subtitle: "Three detailed buyer personas and a journey map showing what content each one needs at every stage.",
      outcome: "Three detailed buyer personas and a journey map.",
      exercises: [
        {
          id: "m2e1",
          number: 1,
          title: "Build Your Primary Buyer",
          instructions: `**Goal of this exercise**\nTo build a highly detailed persona for your most likely buyer.\n\n**What you are doing**\nYou will generate a 7-field persona (Demographics, Pain points, Goals, Objections, Language, Buying triggers, Media habits) using emotional, specific language.\n\n**Prompt:**\n*"I sell [product] to [target]. My campaign goal is: [paste goal]. Build a detailed buyer persona for my PRIMARY customer using the 7 fields. Use realistic details and write the 'Language' section as direct quotes."*`,
          artifact: "Complete primary persona (all 7 fields) with emotional language and customer quotes.",
          artifactRequired: true,
        },
        {
          id: "m2e2",
          number: 2,
          title: "Build Your Secondary Buyer",
          instructions: `**Goal of this exercise**\nTo identify a buyer who purchases your product for entirely different emotional reasons.\n\n**What you are doing**\nYou will build a secondary persona and compare their language and messaging directly against your primary buyer.\n\n**Prompt 1:**\n*"Here is my primary buyer: [paste]. Build a SECONDARY buyer who buys for a different reason. Use the 7-field structure. Add a 'Key Differences' section explaining messaging differences."*\n\n**Prompt 2:**\n*"Create a side-by-side table comparing their language: how each describes the problem, emotional tone, and 3 headline variations tailored to each."*`,
          artifact: "Full secondary persona + Key Differences + side-by-side language comparison table.",
          artifactRequired: true,
        },
        {
          id: "m2e3",
          number: 3,
          title: "The Unexpected Buyer + Journey Map",
          instructions: `**Goal of this exercise**\nTo identify an unexpected demographic and map all three personas to the Awareness, Consideration, and Decision stages.\n\n**What you are doing**\nYou will ask AI for a third out-of-the-box persona, and then create a 3x3 Journey Matrix showing what content moves each persona forward.\n\n**Prompt 1:**\n*"Here are my two personas: [paste]. Suggest a THIRD buyer persona I haven't considered using the 7 fields. Explain why they are worth targeting."*\n\n**Prompt 2:**\n*"Here are my three personas: [paste]. Create a journey stage map as a table (Rows: Awareness, Consideration, Decision / Cols: Personas). Include what they are thinking/feeling, content type, and an example headline."*`,
          artifact: "Third persona (7 fields) + 3×3 journey stage map.",
          artifactRequired: true,
        }
      ]
    },
    {
      number: 3,
      slug: "research-data",
      title: "Research & Data",
      subtitle: "A verified body of research — market gap data, behavior stats, and industry benchmarks — sorted by persuasion power.",
      outcome: "A verified body of research sorted by persuasion power.",
      exercises: [
        {
          id: "m3e1",
          number: 1,
          title: "Prove the Problem Is Real",
          instructions: `**Goal of this exercise**\nTo gather external proof that the problem you solve is widespread and costly.\n\n**What you are doing**\nYou will generate 8-10 compelling statistics and verify their authenticity to ensure you aren't using AI hallucinations.\n\n**Prompt:**\n*"I sell [product] to [audience]. Find 8–10 statistics that prove the problem my product solves is real, widespread, and costly. Include: the number, the source, year, and how I could use it. Format as a table."*`,
          artifact: "Verified table of 8–10 market statistics (stat / source / year / campaign use).",
          artifactRequired: true,
        },
        {
          id: "m3e2",
          number: 2,
          title: "Behavior + Benchmarks",
          instructions: `**Goal of this exercise**\nTo understand your audience's digital habits and your industry's performance averages.\n\n**What you are doing**\nYou will generate two tables: one detailing customer buying behavior and another listing competitive marketing benchmarks.\n\n**Prompt 1 (Behavior):**\n*"My audience is [paste personas]. Find 6–8 stats about how they behave when researching/buying [product]. Include source/year. Format as a table."*\n\n**Prompt 2 (Benchmarks):**\n*"I operate in [industry]. My channels are [list]. Create a competitive benchmark table with average open/click rates, cost per lead, and ROI. Include sources."*`,
          artifact: "Two tables — customer behavior + competitive benchmarks — with sources and years.",
          artifactRequired: true,
        },
        {
          id: "m3e3",
          number: 3,
          title: "Impact Table — Sort by Persuasion",
          instructions: `**Goal of this exercise**\nTo categorize your statistical research into Jaw-Droppers, Credibility Builders, and Emotional Proof.\n\n**What you are doing**\nYou will feed all your verified stats back into AI and have it sort them by their specific marketing utility.\n\n**Prompt:**\n*"Here are all the statistics I've gathered: [paste from Ex 1 & 2]. Sort these into three persuasion tiers: Tier 1 (Jaw-Droppers), Tier 2 (Credibility Builders), Tier 3 (Emotional Proof). For each stat note best content format and which persona it resonates with most. Format as table."*`,
          artifact: "Three-tier impact table (Tier / Stat / Source / Format / Persona).",
          artifactRequired: true,
        }
      ]
    },
    {
      number: 4,
      slug: "content-pipeline",
      title: "Content Pipeline",
      subtitle: "40–50 content topics prioritized by impact, with the top 10 written as production-ready episode descriptions.",
      outcome: "40–50 prioritized content topics and 10 production-ready episode descriptions.",
      exercises: [
        {
          id: "m4e1",
          number: 1,
          title: "Generate the Pipeline",
          instructions: `**Goal of this exercise**\nTo brainstorm a massive list of highly targeted content topics mapped to your personas.\n\n**What you are doing**\nYou will supply your foundational materials to the AI and generate 40-50 topics organized by category.\n\n**Prompt:**\n*"I'm building a content pipeline. Here is my foundation:\\nGOAL: [paste]\\nVOICE: [paste]\\nPERSONAS: [paste]\\nRESEARCH: [paste top 10 stats].\\n\\nGenerate 40–50 content topics organized into 5–7 categories. Include: topic title, category, persona, journey stage, and hook. Categories should emerge naturally."*`,
          artifact: "Topic pipeline table — 40–50 topics × (Category / Title / Persona / Stage / Hook).",
          artifactRequired: true,
        },
        {
          id: "m4e2",
          number: 2,
          title: "Rank by Impact, Map to the Funnel",
          instructions: `**Goal of this exercise**\nTo scientifically score your topics and ensure your content funnel is balanced.\n\n**What you are doing**\nYou will have AI score your topics based on Pain Intensity, Data Support, and Persona Coverage, reordering them highest to lowest.\n\n**Prompt:**\n*"Here is my pipeline: [paste]. Personas: [paste]. Impact table: [paste]. Score each topic (1-5) on: Pain Intensity, Data Support, Persona Coverage. Add the scores and reorder highest to lowest. Format as: Rank / Topic / Pain / Data / Persona / Total / Stage. Flag any journey stage with fewer than 3 topics."*`,
          artifact: "Prioritized topic table + funnel balance analysis + any gap-fill topics.",
          artifactRequired: true,
        },
        {
          id: "m4e3",
          number: 3,
          title: "Write Top 10 Episode Descriptions",
          instructions: `**Goal of this exercise**\nTo produce ready-to-execute content briefs for your 10 most critical topics.\n\n**What you are doing**\nYou will use an 8-part framework (Title, Persona, Stage, Problem, Solution, Data, Hook, CTA) to flesh out your top 10 topics.\n\n**Prompt:**\n*"Here are my top 10 topics: [paste]. My brand voice: [paste]. For each topic, write a full episode description using 8 parts: Title, Target persona, Journey stage, The Problem, The Solution, Key data point, Hook line, CTA. Write in my brand voice."*`,
          artifact: "10 full episode descriptions (Title / Persona / Stage / Problem / Solution / Data / Hook / CTA).",
          artifactRequired: true,
        }
      ]
    },
    {
      number: 5,
      slug: "social-cards",
      title: "Social Cards",
      subtitle: "Three branded social cards for one hook, audited against brand guidelines and ready to ship.",
      outcome: "Three branded social cards ready to ship.",
      exercises: [
        {
          id: "m5e1",
          number: 1,
          title: "Match Hook to Format · Warm-Up",
          instructions: `**Goal of this exercise**\nTo understand which social card styles (Photo-Led, Meme-Style, Bold Stat) best fit specific marketing hooks.\n\n**What you are doing**\nReview the course material on card styles. Stats belong in stat cards. Stories belong in meme or photo cards. Commands belong in meme cards. *This is a group discussion warm-up.*`,
          artifact: "None — this is a group discussion warm-up.",
          artifactRequired: false,
        },
        {
          id: "m5e2",
          number: 2,
          title: "Draft Three Cards",
          instructions: `**Goal of this exercise**\nTo draft identical messaging across three extremely different visual formats.\n\n**What you are doing**\nYou will take one of your strongest Hooks from Session 4 (or use a fallback) and write out the exact text and design directions for a Photo-Led, Meme-Style, and Bold Stat card.\n\n**The Three Styles:**\n1. **Photo-Led:** Headline, Support line, CTA, Image Direction.\n2. **Meme-Style:** Top text, Punchline, CTA, Background color.\n3. **Bold Stat:** Huge number, Context sentence, CTA.`,
          artifact: "Three card drafts (Photo-Led / Meme-Style / Bold Stat) with headline + support + CTA + design notes.",
          artifactRequired: true,
        },
        {
          id: "m5e3",
          number: 3,
          title: "Brand Audit",
          instructions: `**Goal of this exercise**\nTo ensure your assets don't violate your established brand identity.\n\n**What you are doing**\nYou will review your three drafted cards against your custom Brand Color Palette, Typography rules, and Tone of Voice. Then you will open Canva (or similar) and physically design and export the final images.\n\n**Check:**\nDid AI try to give you neon colors instead of warm tones? Are the CTAs too aggressive? Fix them.`,
          artifact: "Finished designed cards (images) — the three social cards ready to ship.",
          artifactRequired: true,
        }
      ]
    },
    {
      number: 6,
      slug: "blog-writing",
      title: "Blog Writing",
      subtitle: "A 5-section blog outline and a fully edited draft that uses a strong stat+story opening.",
      outcome: "A fully edited blog draft ready to publish.",
      exercises: [
        {
          id: "m6e1",
          number: 1,
          title: "Reverse-Engineer a Post · Warm-Up",
          instructions: `**Goal of this exercise**\nTo recognize the 5-Section Problem/Solution framework in action.\n\n**What you are doing**\nYou will examine a sample blog post to easily identify its Hook, Diagnosis, Solution, Proof, and Call to Action sections. *This is a reading warm-up.*`,
          artifact: "None — this is a conceptual warm-up.",
          artifactRequired: false,
        },
        {
          id: "m6e2",
          number: 2,
          title: "Write an Outline",
          instructions: `**Goal of this exercise**\nTo manually skeletonize your blog post to prevent AI from wandering off-topic.\n\n**What you are doing**\nYou will personally write a 5-section outline using your own human voice. Only once completed will you use AI to expand it into paragraphs.\n\n**The Outline:** Hook, Diagnosis, 3-Step Solution, Proof stats, CTA.\n\n**Prompt:**\n*"Expand each section below into 2–3 paragraphs. Keep the tone conversational and specific. Write for [your persona]. [paste outline]."*`,
          artifact: "Completed 5-section outline (ready to become the AI expansion prompt).",
          artifactRequired: true,
        },
        {
          id: "m6e3",
          number: 3,
          title: "Evaluate Openings",
          instructions: `**Goal of this exercise**\nTo mercilessly destroy boring, generic AI introductions.\n\n**What you are doing**\nYou will review your AI-generated blog draft and delete the throat-clearing first paragraph ("In today's digital landscape..."). You will edit the remaining copy for Specificity, Stakes, and Recognition to ensure it grabs attention instantly.`,
          artifact: "Final edited blog draft — first paragraph deleted, edited for quality.",
          artifactRequired: true,
        }
      ]
    },
    {
      number: 7,
      slug: "landing-page",
      title: "Landing Page",
      subtitle: "A scored sample landing page, your own hero section written, and a full 8-section campaign audit.",
      outcome: "Your own hero section written, and a full 8-section campaign audit.",
      exercises: [
        {
          id: "m7e1",
          number: 1,
          title: "Score a Landing Page",
          instructions: `**Goal of this exercise**\nTo train your eye to spot weak landing page elements.\n\n**What you are doing**\nYou will use a 5-point grading criteria (Headline Clarity, Value Prop, Social Proof, CTA Strength, Visual Hierarchy) to evaluate a sample landing page, scoring it out of 25.\n\nProvide a one-paragraph diagnostic on how to fix the broken parts.`,
          artifact: "Your scored evaluation of the sample page (5 criteria × 1–5) + diagnostic.",
          artifactRequired: true,
        },
        {
          id: "m7e2",
          number: 2,
          title: "Write Your Hero Section",
          instructions: `**Goal of this exercise**\nTo craft the most critical 3 seconds of your landing page.\n\n**What you are doing**\nYou will write a hyper-specific Headline (under 12 words), an objection-handling Subheadline, and an action-verb CTA Button.\n\nIf it fails your internal 5-point check, use this **Prompt:**\n*"Rewrite this headline to be more specific about the outcome. My target audience is [persona]. The main pain point is [pain point]."*`,
          artifact: "Your hero section (headline + subheadline + CTA) that passes 3+ of the 5 criteria.",
          artifactRequired: true,
        },
        {
          id: "m7e3",
          number: 3,
          title: "Campaign Audit",
          instructions: `**Goal of this exercise**\nTo verify that your entire campaign links together logically without any dead ends.\n\n**What you are doing**\nYou will audit all 8 campaign sections (Strategy, Avatars, Research, Pipeline, Cards, Blog, Landing Page, Launch) and perform the \"Thread Test\" by tracing a single Persona's journey from social card to checkout.\n\nIf the chain is broken, fix the missing link.`,
          artifact: "Completed 8-section audit + one-persona Thread Test result.",
          artifactRequired: true,
        }
      ]
    },
    {
      number: 8,
      slug: "launch",
      title: "Launch",
      subtitle: "Every asset is connected, all 10 launch items are signed off, and a final 8-section audit confirms the campaign is ready to go live.",
      outcome: "A fully verified, signed-off campaign ready to launch.",
      exercises: [
        {
          id: "m8e1",
          number: 1,
          title: "Connect Your Assets",
          instructions: `**Goal of this exercise**\nTo explicitly define the traffic flow pathways.\n\n**What you are doing**\nYou will document the exact URLs ensuring that Social Cards → Landing Page, and Blog → Landing Page. You will verify that every CTA button actively links to the correct destination and does not route to your dead homepage.`,
          artifact: "Verified link map — every caption and CTA with the exact URL it points to.",
          artifactRequired: true,
        },
        {
          id: "m8e2",
          number: 2,
          title: "Launch Checklist",
          instructions: `**Goal of this exercise**\nTo ensure no critical technical errors kill your launch.\n\n**What you are doing**\nYou will manually execute all 10 GO / NO-GO physical checks. This includes testing on mobile, triggering the forms, clicking every button, verifying analytics tags, and having an external human review the layout.`,
          artifact: "Signed-off 10-item checklist (with a yes/no for each).",
          artifactRequired: true,
        },
        {
          id: "m8e3",
          number: 3,
          title: "Final Campaign Audit",
          instructions: `**Goal of this exercise**\nFinal approval before broadcast.\n\n**What you are doing**\nYou will perform one final sweep. If you hit send on your first social post right now, does every click lead someone closer to converting? If yes → launch.\nIf no → fix before going live.`,
          artifact: "Signed-off final audit confirming all 8 sections are complete and connected — campaign is launch-ready.",
          artifactRequired: true,
        }
      ]
    }
  ]
};
